import { pageTitle } from '@palaccount/config';
import {
  PROJECT_STATUS_TRANSITIONS,
  type MilestoneResponse,
  type PersonResponse,
  type ProjectStatus,
} from '@palaccount/contracts';
import { Flag, MapPin, Package, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr, Pct } from '@/components/amount';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { TableCell } from '@/components/ui/table';
import { Modal } from '@/features/inventory/modal';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { useCan } from '@/lib/permissions';
import { localizeServerCause } from '@/lib/server-messages';
import { useCompanyToday, useDocumentDate } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  useAddMember,
  useAddMilestone,
  useCreateProject,
  usePeople,
  useProject,
  usePagedProjects,
  useRemoveMember,
  useUpdateMilestone,
  useUpdateProject,
} from './api';
import { Pager } from '@/components/pager';
import { usePagedState, usePageRange } from '@/lib/paging';
import { TaskPanel } from '@/features/crm/task-panel';
import { ProjectsTabs } from './projects-tabs';

/**
 * PRJ-01 — Projects List. The two headline defects are fixed at the source
 * and this screen only draws the answers: Spent is DERIVED (billed +
 * unbilled approved value — the same figure Billing shows, BR-PRJ-01-04)
 * and Progress is DERIVED from task completion (BR-PRJ-01-05). Headline
 * figures count Planning/Active/On hold (BR-PRJ-01-02). Projects are
 * creatable and editable (BR-PRJ-01-08) — the client is a CUSTOMER record
 * or Internal, and an internal project is non-billable automatically
 * (§13.1.4). Milestones are ONE list, completable from here
 * (BR-PRJ-01-07, Q-PRJ-01-5).
 *
 * §7's detail pane is THREE stacked cards — details, milestones, and the
 * shared task panel — and the third was missing. BR-PRJ-01-06 wants the
 * SHARED component (the one CRM-01 and CRM-02 already draw), matched by the
 * related-record pointer and not by a projects-only query: a project task is
 * a row in the one task engine carrying `relatedKind: 'project'` and
 * `relatedId` = the project (BR-PRJ-02-01, written at
 * `project-tasks.service.ts:148-149`), so `TaskPanel` finds them with no new
 * route, no new component and no new dictionary key. Importing CRM's panel
 * rather than copying it is the whole point of the rule — a second panel
 * would be a second engine, which is what BR-PRJ-02-01 forbids.
 */

/** PRJ-01 §4's table: 25 rows fill the screen beside the detail card. */
const PROJECT_PAGE_SIZE = 25;

const STATUS_META: Record<ProjectStatus, { key: string; chip: string }> = {
  planning: { key: 'prj.stPlanning', chip: 'bg-muted text-muted-foreground' },
  active: { key: 'prj.stActive', chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' },
  on_hold: { key: 'prj.stOnHold', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  completed: { key: 'prj.stCompleted', chip: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  cancelled: { key: 'prj.stCancelled', chip: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' },
};

const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold';

export function ProjectsPage() {
  const { t } = useTranslation();
  const canEdit = useCan('projects.edit');
  const [selectedId, setSelectedId] = useState<string>();
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | ProjectStatus>('');
  const [managerFilter, setManagerFilter] = useState('');

  useEffect(() => {
    document.title = pageTitle(t('prj.projectsTitle'));
  }, [t]);

  /** NFR-42 — the page is in the URL, and every filter resets it. */
  const paging = usePagedState(PROJECT_PAGE_SIZE);
  const filters = useMemo(
    () => ({
      ...(statusFilter === '' ? {} : { status: statusFilter }),
      ...(managerFilter === '' ? {} : { managerId: managerFilter }),
    }),
    [statusFilter, managerFilter],
  );
  const { data } = usePagedProjects(filters, paging.page, PROJECT_PAGE_SIZE);
  // The rows ARE the filtered set now: the two `.filter()` calls that used to
  // sit here ran after the whole portfolio had been priced (BR-PRJ-01-04's
  // engine, per project) and would have returned the wrong rows under a LIMIT.
  const filtered = data?.projects ?? [];
  const summary = data?.summary;
  const range = usePageRange(data, PROJECT_PAGE_SIZE);
  const { data: detail } = useProject(selectedId);
  const { data: people } = usePeople();

  const act = (promise: Promise<unknown>) => {
    setError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized, a request that never answered gets this screen's own. The
      // rate-card dialog below names the SAME key: one screen, one sentence.
      setError(localizeServerCause(t, cause, 'prj.errSaveFailedProject'));
    });
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('prj.eyebrow')}
        </p>
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-semibold">{t('prj.projectsTitle')}</h1>
          {/* ERR-PRJ-01-01 — "looked for a New project button: there is
              none". PRJ-01 was a read-only mock-up; BR-PRJ-01-08 makes a
              project creatable and editable, and this is the button the row
              says does not exist. Its sibling ERR-PRJ-01-02 ("nothing accepts
              input") is answered by the modal it opens — pinned in
              err-clusters-2-prj01.test.tsx, which types a name and asserts
              THAT STRING reaches the POST body, not merely that the input
              held it. */}
          <Gated permission="projects.create" reasonKey="perm.cannot.createProjects">
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="me-1 h-4 w-4" />
              {t('prj.newProject')}
            </Button>
          </Gated>
        </div>
      </div>
      <ProjectsTabs />

      {error !== '' ? (
        <p role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </p>
      ) : null}

      {/* BR-PRJ-01-02: Planning + Active + On hold only. */}
      {summary ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Headline label={t('prj.hActive')} value={String(summary.activeCount)} />
          <Headline label={t('prj.hBudget')} value={<Amount value={summary.activeBudget} />} />
          <Headline label={t('prj.hSpent')} value={<Amount value={summary.spent} />} />
          <Headline label={t('prj.hAvgProgress')} value={<Pct value={summary.avgProgressPct} />} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <NativeSelect
          aria-label={t('prj.filterStatus')}
          value={statusFilter}
          onChange={(event) =>
            paging.change(setStatusFilter)(event.target.value as '' | ProjectStatus)
          }
          className="w-44"
        >
          <option value="">{t('prj.allStatuses')}</option>
          {Object.entries(STATUS_META).map(([value, meta]) => (
            <option key={value} value={value}>
              {t(meta.key)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label={t('prj.filterManager')}
          value={managerFilter}
          onChange={(event) => paging.change(setManagerFilter)(event.target.value)}
          className="w-44"
        >
          <option value="">{t('prj.allManagers')}</option>
          {(people ?? []).map((person) => (
            <option key={person.id} value={person.id}>
              {person.name}
            </option>
          ))}
        </NativeSelect>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-start text-xs text-muted-foreground">
                  <th className="p-3 text-start">{t('prj.colProject')}</th>
                  <th className="p-3 text-start">{t('prj.colManager')}</th>
                  <th className="p-3 text-start">{t('prj.colStatus')}</th>
                  <th className="p-3 text-start">{t('prj.colProgress')}</th>
                  <th className="p-3 text-end">{t('prj.colSpentBudget')}</th>
                </tr>
              </thead>
              <tbody>
                {/* ERR-PRJ-01-06 — PRJ-01 §14: "Filtered to a status with no
                    projects | The table shows 'No projects match these
                    filters.' | Clear the filter". The spec's own sentence,
                    keyed rather than inlined so it exists in both locales. */}
                {filtered.length === 0 ? (
                  <tr>
                    <TableCell colSpan={5} className="px-6 text-center text-muted-foreground">
                      {t('prj.emptyProjects')}
                    </TableCell>
                  </tr>
                ) : (
                  filtered.map((project) => (
                    <tr
                      key={project.id}
                      onClick={() => setSelectedId(project.id)}
                      className={cn(
                        'cursor-pointer border-b last:border-0 hover:bg-muted/50',
                        selectedId === project.id && 'bg-muted/60',
                      )}
                    >
                      <TableCell className="px-3">
                        <div className="font-medium">{project.name}</div>
                        <div className="text-xs text-muted-foreground">
                          <Ltr value={project.code} /> · {project.clientName}
                        </div>
                      </TableCell>
                      <TableCell className="px-3">{project.managerName ?? '—'}</TableCell>
                      <TableCell className="px-3">
                        <span className={cn(CHIP, STATUS_META[project.status].chip)}>
                          {t(STATUS_META[project.status].key)}
                        </span>
                      </TableCell>
                      <TableCell className="px-3">
                        <ProgressBar pct={project.progressPct} />
                      </TableCell>
                      <TableCell className="px-3 text-end">
                        <Amount value={project.spent} currency={project.currency} />
                        <span className="text-muted-foreground"> / </span>
                        <Amount value={project.budget} currency={project.currency} />
                        {project.overBudget ? (
                          <div className="text-xs font-semibold text-red-600 dark:text-red-400">
                            {t('prj.overBudget')}
                          </div>
                        ) : null}
                      </TableCell>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {(data?.total ?? 0) > 0 ? (
              <Pager
                page={paging.page}
                pages={range.pages}
                first={range.first}
                last={range.last}
                total={range.total}
                onPage={paging.setPage}
                label={t('prj.projectsTitle')}
              />
            ) : null}
          </CardContent>
        </Card>

        {detail ? (
          <ProjectDetail
            key={detail.id}
            detail={detail}
            canEdit={canEdit}
            people={people ?? []}
            act={act}
          />
        ) : (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              {t('prj.pickProject')}
            </CardContent>
          </Card>
        )}
      </div>

      {formOpen ? (
        <NewProjectModal
          onClose={() => setFormOpen(false)}
          people={people ?? []}
          onError={setError}
        />
      ) : null}
    </div>
  );
}

function Headline({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}

function ProgressBar({ pct, danger = false }: { pct: number; danger?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full', danger ? 'bg-red-500' : 'bg-primary')}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <Pct value={pct} className="text-xs tabular-nums" />
    </div>
  );
}

function ProjectDetail({
  detail,
  canEdit,
  people,
  act,
}: {
  detail: NonNullable<ReturnType<typeof useProject>['data']>;
  canEdit: boolean;
  people: PersonResponse[];
  act: (promise: Promise<unknown>) => void;
}) {
  const { t } = useTranslation();
  const updateProject = useUpdateProject();
  const updateMilestone = useUpdateMilestone();
  const addMilestone = useAddMilestone();
  const addMember = useAddMember();
  const removeMember = useRemoveMember();
  const [milestoneName, setMilestoneName] = useState('');
  // G-09: a new milestone's date starts as the company's today.
  const today = useCompanyToday();
  const [milestoneDue, setMilestoneDue] = useDocumentDate();
  const [milestoneAmount, setMilestoneAmount] = useState('');
  const [newMemberId, setNewMemberId] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('Senior Consultant');
  // PRJ-01: the job on site — free text, beside the price band.
  const [newMemberJob, setNewMemberJob] = useState('');

  // §7's transition model: offer only what the state machine allows.
  const nextStatuses = PROJECT_STATUS_TRANSITIONS[detail.status] ?? [];
  const spentPct =
    Number.parseFloat(detail.budget) > 0
      ? Math.round((Number.parseFloat(detail.spent) / Number.parseFloat(detail.budget)) * 100)
      : 0;

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-semibold">{detail.name}</h2>
            <p className="text-xs text-muted-foreground">
              <Ltr value={detail.code} /> · {detail.clientName}
            </p>
          </div>
          {/* No legal next status is the project's own answer. */}
          {nextStatuses.length > 0 ? (
            <Gated permission="projects.edit" reasonKey="perm.cannot.moveProjects">
            <NativeSelect
              aria-label={t('prj.colStatus')}
              value={detail.status}
              onChange={(event) =>
                act(
                  updateProject.mutateAsync({
                    projectId: detail.id,
                    body: { status: event.target.value as ProjectStatus },
                  }),
                )
              }
              className="w-36"
            >
              <option value={detail.status}>{t(STATUS_META[detail.status].key)}</option>
              {nextStatuses.map((status) => (
                <option key={status} value={status}>
                  {t(STATUS_META[status as ProjectStatus].key)}
                </option>
              ))}
            </NativeSelect>
            </Gated>
          ) : (
            <span className={cn(CHIP, STATUS_META[detail.status].chip)}>
              {t(STATUS_META[detail.status].key)}
            </span>
          )}
        </div>

        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t('prj.progress')}</span>
            <Pct value={detail.progressPct} />
          </div>
          <ProgressBar pct={detail.progressPct} />
          <p className="text-xs text-muted-foreground">
            {t('prj.progressDerived', { done: detail.doneTaskCount, total: detail.taskCount })}
          </p>
        </div>

        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t('prj.spentVsBudget')}</span>
            <span>
              <Amount value={detail.spent} currency={detail.currency} /> /{' '}
              <Amount value={detail.budget} currency={detail.currency} />
            </span>
          </div>
          <ProgressBar pct={spentPct} danger={detail.overBudget} />
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">{t('prj.remaining')}</span>
            {detail.overBudget ? (
              <span className="font-semibold text-red-600 dark:text-red-400">{t('prj.overBudget')}</span>
            ) : (
              <Amount value={detail.remaining} currency={detail.currency} />
            )}
          </div>
        </div>

        {/* PRJ-01/G-03 — WHERE the project happens. An installation crew
            goes to a site; a consultancy engagement has none, and then the
            block says so rather than drawing three empty lines. */}
        <div className="space-y-1">
          <h3 className="flex items-center gap-1 text-sm font-medium">
            <MapPin className="h-4 w-4" aria-hidden /> {t('prj.siteSection')}
          </h3>
          <SiteBlock detail={detail} />
        </div>

        {/* Team — each person carries a PRICE BAND (§13.1.3) and, since
            PRJ-01's installation-company ask, a JOB on site. */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">{t('prj.team')}</h3>
          {detail.members.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('prj.noTeam')}</p>
          ) : (
            <ul className="space-y-1">
              {detail.members.map((member) => (
                <li key={member.id} className="flex items-center justify-between text-sm">
                  <span>
                    {member.employeeName ?? '—'}
                    {member.role !== null ? (
                      <span className="ms-2 text-xs font-medium">{member.role}</span>
                    ) : null}
                    <span className="ms-2 text-xs text-muted-foreground">{member.rateRole}</span>
                  </span>
                  {/* GEN-58 DELIBERATELY DOES NOT REACH HERE, and the reason is
                      its own sentence: "Delete this permanently? THIS CANNOT BE
                      UNDONE." Taking somebody off a project team CAN be undone —
                      you add them back. Asking that question here would be the
                      product saying something untrue, which is worse than not
                      asking. This removes a row from a relation, not a record.
                      Recorded so the next GEN-58 sweep does not re-open it. */}
                  <Gated permission="projects.edit" reasonKey="perm.cannot.editProjectTeam">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        act(removeMember.mutateAsync({ projectId: detail.id, memberId: member.id }))
                      }
                    >
                      {t('common.delete')}
                    </Button>
                  </Gated>
                </li>
              ))}
            </ul>
          )}
          <GatedGroup permission="projects.edit" reasonKey="perm.cannot.editProjectTeam">
            <div className="flex flex-wrap items-end gap-2">
              <NativeSelect
                aria-label={t('prj.memberPerson')}
                value={newMemberId}
                onChange={(event) => setNewMemberId(event.target.value)}
                className="w-40"
              >
                <option value="">{t('prj.memberPerson')}</option>
                {people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </NativeSelect>
              <NativeSelect
                aria-label={t('prj.memberRole')}
                value={newMemberRole}
                onChange={(event) => setNewMemberRole(event.target.value)}
                className="w-44"
              >
                {detail.rateCard.map((row) => (
                  <option key={row.id} value={row.roleName}>
                    {row.roleName}
                  </option>
                ))}
              </NativeSelect>
              <Input
                aria-label={t('prj.memberJob')}
                placeholder={t('prj.memberJob')}
                value={newMemberJob}
                onChange={(event) => setNewMemberJob(event.target.value)}
                className="w-36"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={newMemberId === ''}
                onClick={() => {
                  act(
                    addMember.mutateAsync({
                      projectId: detail.id,
                      body: {
                        employeeId: newMemberId,
                        rateRole: newMemberRole,
                        ...(newMemberJob.trim() !== '' ? { role: newMemberJob.trim() } : {}),
                      },
                    }),
                  );
                  setNewMemberId('');
                  setNewMemberJob('');
                }}
              >
                {t('prj.addMember')}
              </Button>
            </div>
          </GatedGroup>
        </div>

        {/* PRJ-01 — Materials: the posted Stock OUT vouchers issued to this
            project (INV-08's project_issue), each a link to its voucher, and
            what they came to — the slice of Spent above that is stock rather
            than hours or invoices. Read-only here: the voucher screen is where
            an issue is raised and posted. */}
        <div className="space-y-2">
          <h3 className="flex items-center justify-between gap-1 text-sm font-medium">
            <span className="flex items-center gap-1">
              <Package className="h-4 w-4" aria-hidden /> {t('prj.materials')}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              {t('prj.materialsTotal')}{' '}
              <Amount value={detail.materialsCost} currency={detail.currency} />
            </span>
          </h3>
          {detail.materials.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('prj.noMaterials')}</p>
          ) : (
            <ul className="space-y-1">
              {detail.materials.map((issue) => (
                <li key={issue.adjustmentId} className="flex items-center justify-between gap-2 text-sm">
                  <span>
                    <Ltr value={issue.adjustmentDate} className="font-mono text-xs text-muted-foreground" />
                    <Link
                      to={`/inventory/adjustments/${issue.adjustmentId}`}
                      className="ms-2 font-mono text-xs text-primary underline-offset-4 hover:underline"
                    >
                      <Ltr value={issue.code} />
                    </Link>
                  </span>
                  <Amount value={issue.cost} currency={issue.currency} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ONE milestone list (Q-PRJ-01-5), completable HERE (BR-PRJ-01-07).

            ERR-PRJ-01-03 — "tried to tick a milestone off: the list is
            read-only" — is answered by the tick below plus the add form
            beneath it. The prototype drew the list and gave it no editor at
            all, which is the defects table's "read-only, with no way to add,
            edit or complete one". */}
        <div className="space-y-2">
          <h3 className="flex items-center gap-1 text-sm font-medium">
            <Flag className="h-4 w-4" /> {t('prj.milestones')}
          </h3>
          {detail.milestones.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('prj.noMilestones')}</p>
          ) : (
            <ul className="space-y-1">
              {detail.milestones.map((milestone) => (
                <MilestoneRow
                  key={milestone.id}
                  milestone={milestone}
                  canEdit={canEdit}
                  onComplete={() =>
                    act(
                      updateMilestone.mutateAsync({
                        milestoneId: milestone.id,
                        body: { status: 'completed' },
                      }),
                    )
                  }
                />
              ))}
            </ul>
          )}
          <GatedGroup permission="projects.edit" reasonKey="perm.cannot.editMilestones">
            <div className="flex flex-wrap items-end gap-2">
              <Input
                aria-label={t('prj.milestoneName')}
                placeholder={t('prj.milestoneName')}
                value={milestoneName}
                onChange={(event) => setMilestoneName(event.target.value)}
                className="w-40"
              />
              <Input
                aria-label={t('prj.milestoneDue')}
                type="date"
                value={milestoneDue}
                onChange={(event) => setMilestoneDue(event.target.value)}
                className="w-36"
              />
              <Input
                aria-label={t('prj.milestoneAmount')}
                placeholder={t('prj.milestoneAmount')}
                inputMode="decimal"
                value={milestoneAmount}
                onChange={(event) => setMilestoneAmount(event.target.value)}
                className="w-28"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={milestoneName.trim() === '' || milestoneDue === ''}
                onClick={() => {
                  act(
                    addMilestone.mutateAsync({
                      projectId: detail.id,
                      body: {
                        name: milestoneName.trim(),
                        dueDate: milestoneDue,
                        ...(milestoneAmount !== '' ? { amount: milestoneAmount } : {}),
                      },
                    }),
                  );
                  setMilestoneName('');
                  setMilestoneDue(today);
                  setMilestoneAmount('');
                }}
              >
                {t('prj.addMilestone')}
              </Button>
            </div>
          </GatedGroup>
        </div>

        {/* The third stacked card (§7): the SHARED task panel, matched by the
            related-record pointer — BR-PRJ-01-06. Same component CRM-01 and
            CRM-02 embed, so a project's tasks read identically wherever they
            are shown, and its Add task button routes to Follow-ups pre-filled
            (BR-CRM-03-15). `detail.code` is the PRJ-#### the task engine
            already stores as `related_key`. */}
        <TaskPanel
          relatedKind="project"
          relatedId={detail.id}
          relatedKey={detail.code}
          relatedName={detail.name}
        />
      </CardContent>
    </Card>
  );
}

/**
 * The site, read back. The pin is a plain "lat,lng" string (shape-checked at
 * the database), so the map link is built from its two halves and offered
 * only when there is one — an address with no pin is still an address.
 */
function SiteBlock({ detail }: { detail: NonNullable<ReturnType<typeof useProject>['data']> }) {
  const { t } = useTranslation();
  if (detail.siteAddress === null && detail.siteCity === null && detail.siteGeo === null) {
    return <p className="text-xs text-muted-foreground">{t('prj.noSite')}</p>;
  }
  const [lat, lng] = (detail.siteGeo ?? '').split(',');
  return (
    <div className="text-sm">
      <p>{[detail.siteAddress, detail.siteCity].filter((part) => part !== null).join(' · ')}</p>
      {detail.siteGeo !== null ? (
        <a
          className="text-xs text-primary underline-offset-4 hover:underline"
          href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`}
          target="_blank"
          rel="noreferrer"
        >
          {t('prj.siteMap')} · <Ltr value={detail.siteGeo} />
        </a>
      ) : null}
    </div>
  );
}

/** A team row on the NEW-project form, before any of it exists on the server. */
interface TeamDraftRow {
  employeeId: string;
  role: string;
}

function MilestoneRow({
  milestone,
  canEdit,
  onComplete,
}: {
  milestone: MilestoneResponse;
  canEdit: boolean;
  onComplete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center justify-between gap-2 text-sm">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={milestone.done}
          disabled={!canEdit || milestone.done}
          onChange={onComplete}
          aria-label={t('prj.completeMilestone', { name: milestone.name })}
        />
        <span className={cn(milestone.done && 'text-muted-foreground line-through')}>
          {milestone.name}
        </span>
      </label>
      <span className="text-xs text-muted-foreground">
        <Ltr value={milestone.dueDate} />
        {milestone.status === 'invoiced' && milestone.invoiceCode !== null ? (
          <span className="ms-2 font-medium text-blue-600 dark:text-blue-400">
            <Ltr value={milestone.invoiceCode} />
          </span>
        ) : null}
      </span>
    </li>
  );
}

function NewProjectModal({
  onClose,
  people,
  onError,
}: {
  onClose: () => void;
  people: PersonResponse[];
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateProject();
  const [name, setName] = useState('');
  const [internal, setInternal] = useState(false);
  const [partnerId, setPartnerId] = useState('');
  // GEN-50 — the client field: customers, searched. The `customer` narrowing
  // is the server's, so a page of matches is a page of CUSTOMERS.
  const customerPicker = usePartnerPicker(partnerId || null, { type: 'customer' });
  const [managerId, setManagerId] = useState('');
  // G-09: a new project starts today; the end date is a plan and stays open.
  const [startDate, setStartDate] = useDocumentDate();
  const [endDate, setEndDate] = useState('');
  const [budget, setBudget] = useState('');
  const [billingType, setBillingType] = useState('time_materials');
  const [flatRate, setFlatRate] = useState('');
  // PRJ-01/G-03 — the site, all optional.
  const [siteAddress, setSiteAddress] = useState('');
  const [siteCity, setSiteCity] = useState('');
  const [siteGeo, setSiteGeo] = useState('');
  // PRJ-01 — the crew, named up front. The people come from the SAME staff
  // census as the manager above (ERR-PRJ-01-05: one list, every picker).
  const [team, setTeam] = useState<TeamDraftRow[]>([]);
  const [teamPick, setTeamPick] = useState('');
  const [teamJob, setTeamJob] = useState('');
  const nameOf = (employeeId: string) => people.find((person) => person.id === employeeId)?.name ?? '—';

  const complete =
    name.trim() !== '' &&
    managerId !== '' &&
    startDate !== '' &&
    endDate !== '' &&
    budget !== '' &&
    (internal || partnerId !== '');

  return (
    <Modal title={t('prj.newProject')} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <Label htmlFor="prj-name">{t('prj.fName')}</Label>
          <Input id="prj-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={internal}
            onChange={(event) => setInternal(event.target.checked)}
          />
          {/* §13.1.4: internal ⇒ non-billable, automatically. */}
          {t('prj.fInternal')}
        </label>
        {internal ? (
          <p className="text-xs text-muted-foreground">{t('prj.internalHint')}</p>
        ) : (
          <div>
            <Label htmlFor="prj-client">{t('prj.fClient')}</Label>
            <PartnerTypeahead
              entity="customer"
              id="prj-client"
              label={t('prj.fClient')}
              value={partnerId || null}
              onChange={(id) => setPartnerId(id ?? '')}
              options={customerPicker.options}
              selected={customerPicker.optionFor(partnerId)}
              query={customerPicker.query}
              onQueryChange={customerPicker.setQuery}
              clearLabel="—"
              emptyLabel={t('picker.noMatchingCustomer')}
              loading={customerPicker.loading}
            />
          </div>
        )}
        <div>
          <Label htmlFor="prj-manager">{t('prj.fManager')}</Label>
          <NativeSelect
            id="prj-manager"
            value={managerId}
            onChange={(event) => setManagerId(event.target.value)}
          >
            <option value="">—</option>
            {people.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="prj-start">{t('prj.fStart')}</Label>
            <Input
              id="prj-start"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="prj-end">{t('prj.fEnd')}</Label>
            <Input
              id="prj-end"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="prj-budget">{t('prj.fBudget')}</Label>
            <Input
              id="prj-budget"
              inputMode="decimal"
              value={budget}
              onChange={(event) => setBudget(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="prj-billing-type">{t('prj.fBillingType')}</Label>
            <NativeSelect
              id="prj-billing-type"
              value={billingType}
              onChange={(event) => setBillingType(event.target.value)}
            >
              <option value="time_materials">{t('prj.btTm')}</option>
              <option value="fixed_price">{t('prj.btFixed')}</option>
              <option value="milestone">{t('prj.btMilestone')}</option>
              <option value="retainer">{t('prj.btRetainer')}</option>
            </NativeSelect>
          </div>
        </div>
        {billingType !== 'time_materials' ? (
          <div>
            <Label htmlFor="prj-flat">{t('prj.fFlatRate')}</Label>
            <Input
              id="prj-flat"
              inputMode="decimal"
              value={flatRate}
              onChange={(event) => setFlatRate(event.target.value)}
            />
          </div>
        ) : null}

        {/* PRJ-01/G-03 — the SITE. Optional throughout: an internal programme
            happens nowhere in particular, and the server stores null, not ''. */}
        <fieldset className="space-y-2 rounded-md border p-3">
          <legend className="px-1 text-xs font-medium text-muted-foreground">
            {t('prj.siteSection')}
          </legend>
          <div>
            <Label htmlFor="prj-site-address">{t('prj.siteAddress')}</Label>
            <Input
              id="prj-site-address"
              value={siteAddress}
              onChange={(event) => setSiteAddress(event.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="prj-site-city">{t('prj.siteCity')}</Label>
              <Input
                id="prj-site-city"
                value={siteCity}
                onChange={(event) => setSiteCity(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="prj-site-geo">{t('prj.siteGeo')}</Label>
              <Input
                id="prj-site-geo"
                dir="ltr"
                inputMode="decimal"
                placeholder={t('prj.siteGeoPlaceholder')}
                value={siteGeo}
                onChange={(event) => setSiteGeo(event.target.value)}
              />
            </div>
          </div>
        </fieldset>

        {/* PRJ-01 — the TEAM, five to ten people with their jobs on site,
            sent as one list (replace-all) with the project. */}
        <fieldset className="space-y-2 rounded-md border p-3">
          <legend className="px-1 text-xs font-medium text-muted-foreground">{t('prj.team')}</legend>
          {team.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('prj.noTeam')}</p>
          ) : (
            <ul className="space-y-1">
              {team.map((row) => (
                <li key={row.employeeId} className="flex items-center gap-2 text-sm">
                  <span className="me-auto">{nameOf(row.employeeId)}</span>
                  <Input
                    aria-label={t('prj.memberJob')}
                    placeholder={t('prj.memberJob')}
                    value={row.role}
                    onChange={(event) =>
                      setTeam((rows) =>
                        rows.map((candidate) =>
                          candidate.employeeId === row.employeeId
                            ? { ...candidate, role: event.target.value }
                            : candidate,
                        ),
                      )
                    }
                    className="h-8 w-36"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    aria-label={t('prj.removeMember', { name: nameOf(row.employeeId) })}
                    title={t('prj.removeMember', { name: nameOf(row.employeeId) })}
                    onClick={() =>
                      setTeam((rows) => rows.filter((candidate) => candidate.employeeId !== row.employeeId))
                    }
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <NativeSelect
              aria-label={t('prj.memberPerson')}
              value={teamPick}
              onChange={(event) => setTeamPick(event.target.value)}
              className="w-40"
            >
              <option value="">{t('prj.memberPerson')}</option>
              {people
                .filter((person) => !team.some((row) => row.employeeId === person.id))
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
            </NativeSelect>
            <Input
              id="prj-team-job"
              aria-label={t('prj.memberJob')}
              placeholder={t('prj.memberJob')}
              value={teamJob}
              onChange={(event) => setTeamJob(event.target.value)}
              className="w-36"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={teamPick === ''}
              onClick={() => {
                setTeam((rows) => [...rows, { employeeId: teamPick, role: teamJob.trim() }]);
                setTeamPick('');
                setTeamJob('');
              }}
            >
              {t('prj.addMember')}
            </Button>
          </div>
        </fieldset>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!complete || create.isPending}
            onClick={() => {
              onError('');
              create
                .mutateAsync({
                  name: name.trim(),
                  ...(internal ? { isInternal: true } : { partnerId }),
                  managerId,
                  startDate,
                  endDate,
                  budget,
                  billingType: billingType as never,
                  ...(flatRate !== '' ? { flatRate } : {}),
                  ...(siteAddress.trim() !== '' ? { siteAddress: siteAddress.trim() } : {}),
                  ...(siteCity.trim() !== '' ? { siteCity: siteCity.trim() } : {}),
                  ...(siteGeo.trim() !== '' ? { siteGeo: siteGeo.trim() } : {}),
                  ...(team.length > 0
                    ? {
                        members: team.map((row) => ({
                          employeeId: row.employeeId,
                          ...(row.role !== '' ? { role: row.role } : {}),
                        })),
                      }
                    : {}),
                })
                .then(onClose)
                .catch((cause: unknown) => {
                  onError(localizeServerCause(t, cause, 'prj.errSaveFailedProject'));
                });
            }}
          >
            {t('prj.createProject')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
